import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Share2, Copy, Check, Download, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';

export const ShareProfileDialog = ({ providerId, name, specialization, role, triggerClassName = '', triggerLabel = 'Share', testId = 'share-profile-btn' }) => {
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);
  const origin = window.location.origin;
  const shareUrl = `${origin}/api/share/provider/${providerId}`;
  const profileUrl = `${origin}/provider/${providerId}`;
  const cleanName = (name || '').trim() || 'Healthcare Provider';
  const displayName = `${role === 'doctor' && !/^dr/i.test(cleanName) ? 'Dr. ' : ''}${cleanName}`;
  const specLabel = (specialization || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const message = `Check out ${displayName}${specLabel ? ` (${specLabel})` : ''} on Physign. Book an appointment here: ${shareUrl}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `physign-${(name || 'provider').replace(/\s+/g, '-').toLowerCase()}-qr.png`;
    a.click();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className={`rounded-lg ${triggerClassName}`} data-testid={testId} onClick={(e) => e.stopPropagation()}>
          <Share2 className="w-4 h-4 mr-2" />{triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-white w-[calc(100%-2rem)] rounded-xl overflow-hidden [&>*]:min-w-0" data-testid="share-profile-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Share {displayName}&apos;s profile</DialogTitle>
          <DialogDescription>Send this profile on WhatsApp, copy the link, or use the QR code.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <div ref={qrRef} className="p-3 bg-white border border-slate-200 rounded-xl" data-testid="share-qr-code">
            <QRCodeCanvas value={profileUrl} size={180} level="M" fgColor="#3B1C73" includeMargin />
          </div>
          <button type="button" onClick={downloadQr} className="text-sm text-[#3B1C73] font-medium inline-flex items-center gap-1.5 hover:underline" data-testid="share-download-qr-btn">
            <Download className="w-4 h-4" />Download QR code
          </button>
        </div>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
          <input readOnly value={shareUrl} className="flex-1 min-w-0 bg-transparent text-sm text-slate-700 outline-none truncate" data-testid="share-link-input" onFocus={(e) => e.target.select()} />
          <Button size="sm" variant="outline" onClick={copyLink} data-testid="share-copy-link-btn">
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            <span className="ml-1.5">{copied ? 'Copied' : 'Copy'}</span>
          </Button>
        </div>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold py-2.5 rounded-lg transition-colors"
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle className="w-5 h-5" />Share on WhatsApp
        </a>
      </DialogContent>
    </Dialog>
  );
};
